import { Button, Empty, Modal } from 'antd'
import { DocumentType, ImageViewer } from 'features/files'
import { Download } from 'lucide-react'
import React from 'react'
import styled from 'styled-components'

import { MultipagePdfViewer } from './MultipagePdfViewer'

interface DocumentModalProps {
  title?: string
  open: boolean
  onClose: () => void
  document?: {
    signedUrl?: string
    type: string
    filename?: string
    title?: string
  }
  width?: number
}

const StyledModal = styled.div`
  .ant-modal-content {
    background: ${({ theme }) => theme.forecast.background1};
    border: 1px solid ${({ theme }) => theme.forecast.background3};
  }

  .ant-modal-header {
    background: ${({ theme }) => theme.forecast.background1};
    border-bottom: 1px solid ${({ theme }) => theme.forecast.background3};
  }

  .ant-modal-title {
    color: ${({ theme }) => theme.forecast.activeText};
    font-size: 1.125rem;
    font-weight: 600;
  }

  .ant-modal-close-x {
    color: ${({ theme }) => theme.forecast.defaultText};
  }

  .ant-modal-body {
    background: ${({ theme }) => theme.forecast.background1};
    padding: 1.5rem;
    max-height: 80vh;
    overflow: hidden;
  }
`

const NonPdfContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2.5rem 1.25rem;
  text-align: center;
  gap: 1.25rem;

  p {
    color: ${({ theme }) => theme.forecast.defaultText};
    margin: 0;
  }
`

export const DocumentModal: React.FC<DocumentModalProps> = ({
  title,
  open,
  onClose,
  document,
  width = 900,
}) => {
  const handleDownload = () => {
    if (!document?.signedUrl) return

    const a = window.document.createElement('a')
    a.href = document.signedUrl
    a.download = document.filename || document.title || 'document'
    window.document.body.appendChild(a)
    a.click()
    window.document.body.removeChild(a)
  }

  const isPdf = document?.type === DocumentType.PDF
  const isImage = document?.type === DocumentType.IMAGE

  return (
    <StyledModal>
      <Modal
        title={title || document?.title}
        open={open}
        onCancel={onClose}
        footer={null}
        width={width}
        centered>
        {document?.signedUrl && isPdf ? (
          <MultipagePdfViewer
            url={document.signedUrl}
            height='70vh'
            showDownload={true}
            filename={document.filename || document.title}
          />
        ) : document?.signedUrl && isImage ? (
          <ImageViewer url={document.signedUrl} height={600} />
        ) : document?.signedUrl ? (
          <NonPdfContainer>
            <p>Preview not available for {document.type.toUpperCase()} files</p>
            <Button
              type='primary'
              icon={<Download size={16} />}
              onClick={handleDownload}>
              Download File
            </Button>
          </NonPdfContainer>
        ) : (
          <Empty description='Document not available' />
        )}
      </Modal>
    </StyledModal>
  )
}
